import { logger } from '../services/logger.ts';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';
import { Turnstile, type TurnstileRef } from '../components/common/Turnstile.tsx';
import { AppleSignInButton } from '../components/AppleSignInButton.tsx';
import { GoogleSignInButton } from '../components/GoogleSignInButton.tsx';
import { XSignInButton } from '../components/XSignInButton.tsx';

export function UserLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileRef>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();

  // Get the redirect path and success message from location state
  const from = location.state?.from || '/markets';
  const successMessage = location.state?.message || null;
  
  // Check for OAuth redirect_uri parameter
  const urlParams = new URLSearchParams(location.search);
  const redirectUri = urlParams.get('redirect_uri');

  // Load saved email from localStorage on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('wayja_remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Handle OAuth redirect: If user is already logged in and has redirect_uri, redirect immediately
  useEffect(() => {
    const handleOAuthRedirect = async () => {
      if (redirectUri && user) {
        try {
          // Validate redirect URI is a valid URL
          let redirectUrl: URL;
          try {
            redirectUrl = new URL(redirectUri);
          } catch {
            logger.error('Invalid redirect_uri format:', redirectUri);
            setError('Invalid redirect URL. Please contact support.');
            return;
          }

          // Get the current session token
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token && session.user) {
            // Redirect back to external site with token
            redirectUrl.searchParams.set('wayja_token', session.access_token);
            redirectUrl.searchParams.set('wayja_user_id', session.user.id);
            window.location.href = redirectUrl.toString();
          }
        } catch (err) {
          logger.error('Error handling OAuth redirect:', err);
          setError('Failed to redirect. Please try again.');
        }
      }
    };

    handleOAuthRedirect();
  }, [redirectUri, user]);

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setResendingEmail(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`
        }
      });

      if (resendError) {
        // Handle rate limit specifically
        if (resendError.message?.includes('rate_limit') || resendError.message?.includes('5 seconds')) {
          throw new Error('Please wait 5 seconds before requesting another email.');
        }
        throw resendError;
      }

      setEmailResent(true);
      setError('');
      setResendCooldown(5); // 5-second cooldown
      logger.info('Email verification resent successfully', { email });
    } catch (err) {
      logger.error('Error resending confirmation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend confirmation email. Please try again.';
      setError(errorMessage);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setEmailResent(false);

    try {
      // Validate input
      if (!email.trim() || !password.trim()) {
        throw new Error('Please enter both email and password');
      }

      // Wait for Turnstile verification
      if (!captchaToken) {
        setLoading(false);
        setError('Please complete the security verification above before signing in.');
        return;
      }

      // Attempt to sign in
      await signIn(email, password);
      
      // Get user data to check role and email verification
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // Check if email is verified
      if (currentUser?.email_confirmed_at === null) {
        logger.logFailedLogin(email, 'Email not confirmed', {
          reason: 'unverified_email',
          userId: currentUser?.id,
        });
        throw new Error('Email not confirmed');
      }
      
      // Check if user is blocked
      if (currentUser?.user_metadata?.is_blocked) {
        logger.logFailedLogin(email, 'User is blocked', {
          reason: 'blocked_user',
          userId: currentUser?.id,
        });
        throw new Error('User is blocked');
      }
      
      // Save or remove email based on rememberMe state (only after successful login)
      if (rememberMe) {
        localStorage.setItem('wayja_remembered_email', email);
      } else {
        localStorage.removeItem('wayja_remembered_email');
      }
      
      // Log successful login with additional context
      logger.info('User login successful', {
        email,
        userId: currentUser?.id,
        role: currentUser?.app_metadata?.role,
        from: from,
        hasRedirectUri: !!redirectUri,
      });
      
      // Handle OAuth redirect: If redirect_uri is present, redirect back with token
      if (redirectUri) {
        try {
          // Validate redirect URI is a valid URL
          let redirectUrl: URL | null = null;
          try {
            redirectUrl = new URL(redirectUri);
          } catch {
            logger.error('Invalid redirect_uri format after login:', redirectUri);
            // Fall through to normal redirect if redirect URI is invalid
          }

          if (redirectUrl) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token && session.user) {
              // Redirect back to external site with token
              redirectUrl.searchParams.set('wayja_token', session.access_token);
              redirectUrl.searchParams.set('wayja_user_id', session.user.id);
              window.location.href = redirectUrl.toString();
              return; // Don't navigate internally
            }
          }
        } catch (err) {
          logger.error('Error handling OAuth redirect after login:', err);
          // Fall through to normal redirect if OAuth redirect fails
        }
      }
      
      // Normal redirect based on user role (no OAuth redirect)
      if (currentUser?.app_metadata?.role === 'admin' || currentUser?.app_metadata?.role === 'superuser') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      // Convert error to string message for display
      let errorMessage = 'An error occurred during sign in. Please try again.';
      
      if (err instanceof Error) {
        if (err.message.includes('Email not confirmed')) {
          errorMessage = 'Your email needs to be verified. Please check your inbox and spam/junk folder.';
        } else if (err.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (err.message.includes('User is blocked')) {
          errorMessage = 'Your account has been blocked. Please contact support@wayja.me for more information.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      // Reset CAPTCHA on error
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Wayja</h1>
          <p className="text-gray-600">Sign in to start trading</p>
        </div>

        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {error && (
          <div className={`mb-4 p-4 rounded ${
            error.includes('complete the security verification') || error.includes('Security verification')
              ? 'bg-yellow-50 border border-yellow-400 text-yellow-800'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <p>{error}</p>
            {(error.includes('confirm your email') || error.includes('needs to be verified')) && !emailResent && (
              <div className="mt-3 space-y-2">
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resendingEmail || resendCooldown > 0}
                  className="w-full px-4 py-2 bg-wayja-teal text-white text-sm font-medium rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingEmail 
                    ? 'Sending...' 
                    : resendCooldown > 0 
                      ? `Wait ${resendCooldown}s` 
                      : 'Resend confirmation email'
                  }
                </button>
                <div className="text-xs text-gray-500 space-y-1">
                  <p className="text-center">
                    Email not in inbox? Check your spam/junk folder. Email providers sometimes block automated messages.
                  </p>
                  <p className="text-center">
                    Still can't find it? Contact us at{' '}
                    <a href="mailto:support@wayja.me" className="text-wayja-teal hover:underline">
                      support@wayja.me
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {emailResent && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            Confirmation email has been resent. Please check your inbox.
          </div>
        )}

        {/* X Sign-In Button */}
        <div className="mb-4">
          <XSignInButton 
            onSuccess={() => {
              logger.info('X Sign-In successful, user will be redirected');
            }}
            onError={(error) => {
              setError(error);
            }}
          />
        </div>

        {/* Google Sign-In Button */}
        <div className="mb-4">
          <GoogleSignInButton 
            onSuccess={() => {
              logger.info('Google Sign-In successful, user will be redirected');
            }}
            onError={(error) => {
              setError(error);
            }}
          />
        </div>

        {/* Apple Sign-In Button */}
        <div className="mb-6">
          <AppleSignInButton 
            onSuccess={() => {
              logger.info('Apple Sign-In successful, user will be redirected');
            }}
            onError={(error) => {
              setError(error);
            }}
          />
        </div>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                placeholder="Enter your email"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-wayja-teal focus:ring-wayja-teal border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>
            <Link to="/auth/reset-password" className="text-sm text-wayja-teal hover:text-wayja-teal-dark">
              Forgot password?
            </Link>
          </div>

          {/* Cloudflare Turnstile verification - visible to users */}
          <div className="flex flex-col items-center space-y-2">
            <Turnstile
              ref={captchaRef}
              size="normal"
              onVerify={(token) => {
                setCaptchaToken(token);
                // Clear any verification-related errors when successful
                if (error.includes('Security verification') || error.includes('Verification')) {
                  setError('');
                }
              }}
              onError={() => {
                setCaptchaToken(null);
                setError('Security verification failed. Please click the checkbox above to retry.');
              }}
              onExpire={() => {
                setCaptchaToken(null);
                setError('Security verification expired. Please complete the verification above.');
              }}
            />
          </div>

          <button
            type="submit"
            className="w-full bg-wayja-teal text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !captchaToken}
          >
            {loading ? 'Signing in...' : !captchaToken ? 'Complete verification to sign in' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/auth/register" className="text-wayja-teal hover:text-wayja-teal-dark">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}